import React, { useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './BlockedDatesCalendar.css';
import { formatLocalYYYYMMDD, parseDateOnlyLocal } from '../utils/dateUtils';

export interface BlockedDatesCalendarProps {
  dates: string[];
  /** If set, clicking a highlighted day calls this (e.g. remove manual block). */
  onRemoveDate?: (date: string) => void;
  compact?: boolean;
  className?: string;
}

const BlockedDatesCalendar: React.FC<BlockedDatesCalendarProps> = ({
  dates,
  onRemoveDate,
  compact = false,
  className = '',
}) => {
  const dateSet = useMemo(() => new Set(dates), [dates]);

  const highlightDates = useMemo(
    () =>
      dates
        .map(parseDateOnlyLocal)
        .filter((d) => !Number.isNaN(d.getTime())),
    [dates]
  );

  const openToDate = useMemo(() => {
    if (highlightDates.length === 0) return new Date();
    return highlightDates.reduce((earliest, d) => (d < earliest ? d : earliest));
  }, [highlightDates]);

  if (dates.length === 0) {
    return <p className="blocked-calendar-empty">No blocked dates in this view.</p>;
  }

  return (
    <div
      className={`blocked-dates-calendar${compact ? ' blocked-dates-calendar--compact' : ''}${className ? ` ${className}` : ''}`}
    >
      <DatePicker
        key={openToDate.toISOString()}
        inline
        selected={null}
        openToDate={openToDate}
        onChange={(date: Date | null) => {
          if (!date || !onRemoveDate) return;
          const str = formatLocalYYYYMMDD(date);
          if (dateSet.has(str)) onRemoveDate(str);
        }}
        highlightDates={highlightDates}
        dayClassName={(date) =>
          dateSet.has(formatLocalYYYYMMDD(date)) ? 'blocked-calendar-day' : ''
        }
        monthsShown={compact ? 1 : 2}
        disabledKeyboardNavigation
        calendarClassName="blocked-dates-calendar-picker"
      />
      <div className="blocked-calendar-footer">
        <span className="blocked-calendar-legend">
          <span className="blocked-calendar-legend-swatch" aria-hidden />
          Blocked ({dates.length} {dates.length === 1 ? 'day' : 'days'})
        </span>
        {onRemoveDate && (
          <span className="blocked-calendar-hint">Click a highlighted day to remove that block.</span>
        )}
      </div>
    </div>
  );
};

export default BlockedDatesCalendar;
